import React, { useState, useEffect } from 'react';
import { 
  Database, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCw, 
  Loader2,
  Info,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { FormField, Select } from '../../../components/ui/Form';
import { useAccountInitialization } from '../../../hooks/useAccountInitialization';
import { useSettings } from '../../../hooks/useSettings';
import type { AccountingFramework } from '../../../types/account-initialization';
import toast from 'react-hot-toast';

interface AccountInitializationCardProps {
  /** Callback appelé après une initialisation réussie */
  onInitialized?: () => void;
}

/**
 * Carte de gestion de l'initialisation des comptes
 * 
 * Affiche:
 * - Statut d'initialisation de l'exercice courant
 * - Sélection du plan comptable
 * - Actions d'initialisation/réinitialisation
 * - Statistiques après initialisation
 */
export function AccountInitializationCard({ onInitialized }: AccountInitializationCardProps) {
  const { settings } = useSettings();
  const {
    charts,
    currentStatus,
    currentFiscalYear,
    loading,
    initializing,
    error,
    initializeAccounts,
    reinitializeAccounts,
    isCurrentFiscalYearInitialized,
    currentFramework
  } = useAccountInitialization();

  // Utiliser le plan comptable par défaut des settings si disponible
  const defaultFramework = (settings?.accounting?.accountingFramework as AccountingFramework) || 'SYSCOHADA_NORMAL';
  const [selectedFramework, setSelectedFramework] = useState<AccountingFramework>(defaultFramework);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [postableOnly, setPostableOnly] = useState(false);
  
  // Synchroniser avec les settings quand ils changent
  useEffect(() => {
    if (settings?.accounting?.accountingFramework && !currentStatus?.isInitialized) {
      setSelectedFramework(settings.accounting.accountingFramework as AccountingFramework);
    }
  }, [settings?.accounting?.accountingFramework, currentStatus?.isInitialized]);
  const [confirmReinit, setConfirmReinit] = useState(false);

  const handleInitialize = async () => {
    const result = await initializeAccounts(selectedFramework, {
      overwrite: false,
      postableOnly
    });

    if (result) {
      toast.success(`${result.accountsCreated} comptes initialisés avec succès !`);
      onInitialized?.();
    } else {
      toast.error(error || 'Erreur lors de l\'initialisation');
    }
  };

  const handleReinitialize = async () => {
    if (!currentFiscalYear) return;
    
    if (!confirmReinit) {
      setConfirmReinit(true);
      return;
    }

    const result = await reinitializeAccounts(currentFiscalYear.id, selectedFramework);

    if (result) {
      toast.success(`Plan comptable réinitialisé avec ${result.accountsCreated} comptes`);
      setConfirmReinit(false);
      onInitialized?.();
    } else {
      toast.error(error || 'Erreur lors de la réinitialisation');
    }
  };

  // Récupérer les infos du chart sélectionné
  const selectedChart = charts.find(c => c.framework === selectedFramework);

  if (!currentFiscalYear) {
    return (
      <Card title="Initialisation des comptes" icon={Database}>
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <AlertCircle className="w-5 h-5" />
          <p>Veuillez d'abord créer ou sélectionner un exercice fiscal.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Initialisation des comptes - Exercice courant" icon={Database}>
      <div className="space-y-6">
        {/* Description de la section */}
        <p className="text-sm text-muted-foreground">
          Créez les comptes du plan comptable pour l'exercice fiscal sélectionné. 
          Cette action crée la structure des comptes que vous utiliserez pour vos écritures.
        </p>
        {/* Statut de l'exercice courant */}
        <div className={`p-4 rounded-lg border ${
          isCurrentFiscalYearInitialized 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        }`}>
          <div className="flex items-start gap-3">
            {isCurrentFiscalYearInitialized ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${
                isCurrentFiscalYearInitialized 
                  ? 'text-green-700 dark:text-green-300' 
                  : 'text-amber-700 dark:text-amber-300'
              }`}>
                {isCurrentFiscalYearInitialized 
                  ? 'Comptes initialisés' 
                  : 'Comptes non initialisés'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Exercice: <strong>{currentFiscalYear.code}</strong>
              </p>
              {isCurrentFiscalYearInitialized && currentStatus && (
                <div className="mt-2 text-sm space-y-1">
                  <p>Plan comptable: <strong>{currentStatus.framework}</strong></p>
                  <p>Nombre de comptes: <strong>{currentStatus.accountCount}</strong></p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sélection du plan comptable */}
        <div className="space-y-4">
          <FormField label="Plan comptable">
            <Select
              value={selectedFramework}
              onChange={(e) => setSelectedFramework(e.target.value as AccountingFramework)}
              options={charts.map(chart => ({
                value: chart.framework,
                label: `${chart.name} (${chart.accountCount} comptes)`
              }))}
              disabled={initializing}
            />
          </FormField>

          {/* Description du plan sélectionné */}
          {selectedChart && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {selectedChart.description}
                </p>
              </div>
            </div>
          )}

          {/* Options avancées */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Options avancées
          </button>

          {showAdvanced && (
            <div className="pl-4 border-l-2 border-border space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={postableOnly}
                  onChange={(e) => setPostableOnly(e.target.checked)}
                  className="rounded border-border"
                  disabled={initializing}
                />
                <span className="text-sm">
                  Créer uniquement les comptes postables (sans les comptes parents)
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Erreur */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          {!isCurrentFiscalYearInitialized ? (
            <Button
              onClick={handleInitialize}
              disabled={initializing || loading}
              icon={initializing ? Loader2 : Database}
            >
              {initializing ? 'Initialisation...' : 'Initialiser les comptes'}
            </Button>
          ) : (
            <>
              <Button
                variant={confirmReinit ? 'danger' : 'secondary'}
                onClick={handleReinitialize}
                disabled={initializing || loading}
                icon={initializing ? Loader2 : RefreshCw}
              >
                {confirmReinit 
                  ? 'Confirmer la réinitialisation' 
                  : 'Réinitialiser avec un autre plan'}
              </Button>
              {confirmReinit && (
                <Button
                  variant="ghost"
                  onClick={() => setConfirmReinit(false)}
                  disabled={initializing}
                >
                  Annuler
                </Button>
              )}
            </>
          )}
        </div>

        {/* Avertissement pour réinitialisation */}
        {confirmReinit && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              <strong>Attention :</strong> La réinitialisation supprimera tous les comptes existants 
              et en créera de nouveaux. Cette action est irréversible si des écritures existent.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
